import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Moment } from 'moment';

import { DateTimeService } from 'app/services/date-time.service';
import {
  DATE_RANGE_PRESET_OPTIONS,
  DateRangePreset,
  toDatePreset,
} from 'app/shared/utils/date-range-preset';

export interface DateRangeFilterSelection {
  preset: DateRangePreset;
  fromDate: Moment;
  toDate: Moment;
}

export interface DateRangeFilterValidationError {
  code: 'endBeforeStart' | 'maxSpanExceeded';
  message: string;
  fromDate: Moment;
  toDate: Moment;
  maxSpanDays?: number;
}

@Component({
  selector: 'fuse-shared-date-range-filter',
  templateUrl: './date-range-filter.component.html',
  styleUrls: ['./date-range-filter.component.scss'],
})
export class DateRangeFilterComponent implements OnChanges {
  @Input() preset: DateRangePreset = 'custom';
  @Input() fromDate: Moment | null = null;
  @Input() toDate: Moment | null = null;
  @Input() disabled = false;
  @Input() maxSpanDays: number | null = null;
  @Input() referenceDate: Moment | null = null;
  @Input() rangeLabel = 'Date Range';

  @Output() rangeChange = new EventEmitter<DateRangeFilterSelection>();
  @Output() validationError =
    new EventEmitter<DateRangeFilterValidationError>();

  public readonly presetOptions = DATE_RANGE_PRESET_OPTIONS;
  public selectedPreset: DateRangePreset = 'custom';
  public draftFromDate: Moment | null = null;
  public draftToDate: Moment | null = null;

  constructor(private readonly dateTimeService: DateTimeService) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.preset) {
      this.selectedPreset = this.preset;
    }
    if (changes.fromDate) {
      this.draftFromDate = this.cloneOrNull(this.fromDate);
    }
    if (changes.toDate) {
      this.draftToDate = this.cloneOrNull(this.toDate);
    }
  }

  public onStartDateChange(fromDate: Moment | null): void {
    this.draftFromDate = this.cloneOrNull(fromDate);
    this.draftToDate = null;
  }

  public onEndDateChange(toDate: Moment | null): void {
    this.draftToDate = this.cloneOrNull(toDate);
  }

  public onPickerClosed(): void {
    if (!this.draftFromDate) return;

    const toDate = this.draftToDate || this.draftFromDate;
    if (this.isCommittedRange(this.draftFromDate, toDate)) return;

    this.commitRange('custom', this.draftFromDate, toDate);
  }

  public onPresetChange(preset: DateRangePreset): void {
    const datePreset = toDatePreset(preset);
    if (!datePreset) {
      this.selectedPreset = 'custom';
      return;
    }

    const { fromDate, toDate } = this.referenceDate
      ? this.dateTimeService.getDateRange(
          datePreset,
          this.referenceDate.clone()
        )
      : this.dateTimeService.getDateRange(datePreset);
    this.commitRange(preset, fromDate, toDate);
  }

  private commitRange(
    preset: DateRangePreset,
    fromDate: Moment,
    toDate: Moment
  ): void {
    const start = fromDate.clone().startOf('day');
    const end = toDate.clone().endOf('day');
    const error = this.getValidationError(start, end);
    if (error) {
      this.validationError.emit(error);
      this.restoreCommittedRange();
      return;
    }

    this.selectedPreset = preset;
    this.draftFromDate = start.clone();
    this.draftToDate = end.clone();
    this.rangeChange.emit({
      preset,
      fromDate: start.clone(),
      toDate: end.clone(),
    });
  }

  private getValidationError(
    fromDate: Moment,
    toDate: Moment
  ): DateRangeFilterValidationError | null {
    const fromDay = fromDate.clone().startOf('day');
    const toDay = toDate.clone().startOf('day');
    if (toDay.isBefore(fromDay)) {
      return {
        code: 'endBeforeStart',
        message: 'The end date must be on or after the start date.',
        fromDate: fromDate.clone(),
        toDate: toDate.clone(),
      };
    }

    const inclusiveSpanDays = toDay.diff(fromDay, 'days') + 1;
    if (
      this.maxSpanDays != null &&
      this.maxSpanDays > 0 &&
      inclusiveSpanDays > this.maxSpanDays
    ) {
      return {
        code: 'maxSpanExceeded',
        message: `The date range cannot exceed ${this.maxSpanDays} days.`,
        fromDate: fromDate.clone(),
        toDate: toDate.clone(),
        maxSpanDays: this.maxSpanDays,
      };
    }

    return null;
  }

  private cloneOrNull(value: Moment | null): Moment | null {
    return value ? value.clone() : null;
  }

  private isCommittedRange(fromDate: Moment, toDate: Moment): boolean {
    return !!(
      this.fromDate &&
      this.toDate &&
      fromDate.isSame(this.fromDate, 'day') &&
      toDate.isSame(this.toDate, 'day')
    );
  }

  private restoreCommittedRange(): void {
    this.draftFromDate = this.cloneOrNull(this.fromDate);
    this.draftToDate = this.cloneOrNull(this.toDate);
    this.selectedPreset = this.preset;
  }
}
